import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, authHeaders, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import OfferCard from "@/components/OfferCard";
import { ArrowLeft, Store, Phone, MapPin, Clock, Star, Heart, Navigation, Loader2, Globe, Flag, BadgeCheck, CalendarClock, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { getStoreStatus } from "@/lib/storeStatus";
import { timeAgo } from "@/lib/timeAgo";

export default function StorePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { client, refreshClient } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [gMeta, setGMeta] = useState({});
  const [reportOpen, setReportOpen] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [report, setReport] = useState({ reason: "", note: "" });
  const [sendingReport, setSendingReport] = useState(false);
  const [rev, setRev] = useState({ reviews: [], groups: [], average: null, count: 0, can_review: false, has_redeemed: false, reviewable_count: 0, my_review: null, my_review_count: 0 });
  const [reviewOpen, setReviewOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [reviewFilter, setReviewFilter] = useState("all");
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState("");
  const [savingReview, setSavingReview] = useState(false);

  const loadReviews = () => api.get(`/stores/${id}/reviews`, { headers: getToken("client") ? authHeaders("client") : {} })
    .then((r) => setRev(r.data)).catch(() => {});

  useEffect(() => {
    api.get(`/stores/${id}`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
    api.get(`/stores/${id}/google-reviews`).then((r) => { setReviews(r.data.reviews || []); setGMeta(r.data || {}); }).catch(() => {});
    api.get("/report-reasons").then((r) => setReasons(r.data.reasons || r.data || [])).catch(() => {});
    loadReviews();
    // eslint-disable-next-line
  }, [id]);

  const openReview = () => {
    if (!getToken("client")) { toast.info("Accedi per lasciare una recensione"); navigate("/coupons"); return; }
    setMyRating(0);
    setMyComment("");
    setReviewOpen(true);
  };
  const submitReview = async () => {
    if (myRating < 1) { toast.error("Seleziona una valutazione"); return; }
    setSavingReview(true);
    try {
      await api.post(`/stores/${id}/reviews`, { rating: myRating, comment: myComment }, { headers: authHeaders("client") });
      toast.success("Grazie per la tua recensione!");
      setReviewOpen(false);
      loadReviews();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setSavingReview(false); }
  };


  const submitReport = async () => {
    if (!report.reason) { toast.error("Scegli un motivo"); return; }
    setSendingReport(true);
    try {
      await api.post("/reports", { vendor_id: id, reason: report.reason, note: report.note }, { headers: getToken("client") ? authHeaders("client") : {} });
      toast.success("Segnalazione inviata. Grazie!");
      setReportOpen(false);
      setReport({ reason: "", note: "" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setSendingReport(false); }
  };

  const following = new Set((client && client.following) || []).has(id);

  const toggleFollow = async () => {
    if (!getToken("client")) { toast.info("Accedi per seguire i negozi"); navigate("/coupons"); return; }
    try {
      const r = await api.post(`/client/follow/${id}`, {}, { headers: authHeaders("client") });
      toast.success(r.data.following ? "Ora segui questo negozio" : "Hai smesso di seguire");
      refreshClient();
    } catch (_) {}
  };

  if (loading) return <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#E63946]" /></div>;
  if (!data) return <div className="py-24 text-center text-slate-500">Negozio non trovato.</div>;

  const s = data.store;
  const cover = s.cover_photo || "";

  return (
    <div className="kn-fade-up -mx-4 -my-4">
      <div className="relative h-52">
        {cover
          ? <img src={cover} alt="" className="w-full h-full object-cover" />
          : <div className="w-full h-full bg-gradient-to-br from-[#E63946] via-[#c92b3a] to-[#7f1d2b]" />}
        <div className="absolute inset-0 kn-hero-scrim" />
        <button onClick={() => navigate(-1)} data-testid="store-back" className="absolute top-4 left-4 h-10 w-10 rounded-full bg-white/90 flex items-center justify-center shadow">
          <ArrowLeft className="h-5 w-5 text-slate-700" />
        </button>
      </div>

      <div className="px-4 -mt-10 relative">
        <div className="flex items-start justify-between gap-3">
          <div className="h-20 w-20 rounded-2xl bg-white border-4 border-white shadow-lg overflow-hidden flex items-center justify-center shrink-0">
            {s.logo ? <img src={s.logo} alt="" className="h-full w-full object-cover" /> : <Store className="h-8 w-8 text-slate-300" />}
          </div>
          {/* Review slots in top-right — stessa linea, cliccabili (scroll alle recensioni) */}
          <div className="flex flex-col items-end gap-1 pt-10 shrink-0">
            {s.review_avg != null && (
              <button
                type="button"
                data-testid="store-internal-rating"
                onClick={() => {
                  const el = document.querySelector('[data-testid="client-reviews"]');
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFE4E6] text-[#BE123C] text-xs font-bold hover:bg-[#FFD9DE] transition-colors"
              >
                <Star className="h-3.5 w-3.5 fill-[#E63946] text-[#E63946]" /> {s.review_avg}
                <span className="text-[#BE123C]/70 font-medium">({s.review_count})</span>
              </button>
            )}
            {s.google_rating && (
              <button
                type="button"
                data-testid="store-google-rating"
                onClick={() => {
                  const el = document.querySelector('[data-testid="client-reviews"]');
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-bold hover:bg-amber-100 transition-colors"
              >
                <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" /> {s.google_rating}
                <span className="text-amber-500/70 font-medium">({s.google_review_count} G)</span>
              </button>
            )}
          </div>
        </div>

        {/* Pulsanti azione compatti — sopra il nome, tra immagine profilo e recensioni */}
        <div className="mt-3 flex items-center gap-1.5 flex-wrap" data-testid="store-actions-row">
          <button
            type="button"
            onClick={toggleFollow}
            data-testid="follow-store-btn"
            className={`h-8 px-3 rounded-full inline-flex items-center gap-1 text-xs font-bold transition-colors ${following ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-[#E63946] text-white hover:bg-[#d32f3c]"}`}
          >
            <Heart className={`h-3.5 w-3.5 ${following ? "fill-white" : ""}`} /> {following ? "Seguendo" : "Segui"}
          </button>
          {s.phone && (
            <a
              href={`tel:${s.phone}`}
              data-testid="call-store-btn"
              className="h-8 px-3 rounded-full border border-slate-200 inline-flex items-center gap-1 text-xs font-bold text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <Phone className="h-3.5 w-3.5" /> Chiama
            </a>
          )}
          {s.lat && (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${s.lat},${s.lng}`}
              target="_blank"
              rel="noreferrer"
              data-testid="directions-btn"
              className="h-8 px-3 rounded-full border border-slate-200 inline-flex items-center gap-1 text-xs font-bold text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <Navigation className="h-3.5 w-3.5" /> Mappa
            </a>
          )}
        </div>

        <div className="mt-2 min-w-0">
          <h1 className="text-xl font-extrabold text-slate-900 flex items-center gap-1.5" data-testid="store-name">
            <span className="truncate">{s.store_name}</span>
            {s.verified && <BadgeCheck className="h-5 w-5 text-emerald-600 shrink-0" data-testid="store-verified-icon" title="Negozio verificato" />}
          </h1>
          {s.description && (
            <div className="mt-2 rounded-xl bg-slate-50 border border-slate-100 p-3 flex items-start gap-2" data-testid="store-description">
              <Store className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
              <p className="text-slate-600 text-sm leading-relaxed italic">"{s.description}"</p>
            </div>
          )}
          <p className="text-sm text-slate-500 mt-2">{s.category_label}</p>
        </div>

        {(() => {
          const st = getStoreStatus(s.hours_struct, s.special_hours);
          if (!st) return null;
          return (
            <div className={`mt-3 mr-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold ${st.open ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid="store-open-badge">
              <span className={`h-2 w-2 rounded-full ${st.open ? "bg-emerald-500" : "bg-slate-400"}`} />
              {st.label}{st.detail ? ` · ${st.detail}` : ""}
            </div>
          );
        })()}

        {/* Offerte attive — tra la categoria/stato e la galleria foto */}
        <div className="mt-5" data-testid="store-active-offers-block">
          <h2 className="font-extrabold text-lg text-slate-900 mb-3">Offerte attive ({data.active_count})</h2>
          {data.offers.length === 0 ? (
            <div className="text-center py-6 text-slate-400 border border-dashed border-slate-200 rounded-xl">
              <Globe className="h-7 w-7 mx-auto mb-1.5 opacity-40" />
              <p className="text-sm">Nessuna offerta attiva al momento.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 auto-rows-fr">
              {data.offers.map((o) => <div key={o.offer_id} id={`offer-${o.offer_id}`} className="h-full"><OfferCard offer={o} /></div>)}
            </div>
          )}
        </div>

        {s.gallery && s.gallery.length > 0 && (
          <div className="mt-4 -mx-4 px-4" data-testid="store-gallery">
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
              {s.gallery.map((g, i) => (
                <img key={i} src={g} alt="" className="h-28 w-40 object-cover rounded-xl shrink-0 border border-slate-100" />
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 grid gap-2 text-sm">
          {(s.address || s.city) && (
            <div className="flex items-center gap-2 text-slate-600"><MapPin className="h-4 w-4 text-slate-400" /> {[s.address, s.city].filter(Boolean).join(", ")}</div>
          )}
          {Array.isArray(s.hours_struct) && s.hours_struct.length > 0 ? (
            <div className="flex items-start gap-2 text-slate-600" data-testid="store-hours">
              <Clock className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
              <div className="flex-1 grid grid-cols-1 gap-0.5">
                {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((dn, i) => {
                  const d = s.hours_struct[i] || {};
                  const ranges = (d.ranges || []).filter((r) => r && r.from);
                  const today = ((new Date().getDay() + 6) % 7) === i;
                  return (
                    <div key={i} className={`flex justify-between gap-3 text-sm ${today ? "font-bold text-slate-900" : "text-slate-500"}`}>
                      <span>{dn}</span>
                      <span className="text-right">{d.closed || !ranges.length ? "Chiuso" : ranges.map((r) => `${r.from}–${r.to}`).join(", ")}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : s.hours ? (
            <div className="flex items-center gap-2 text-slate-600"><Clock className="h-4 w-4 text-slate-400" /> {s.hours}</div>
          ) : null}
          {s.phone && <div className="flex items-center gap-2 text-slate-600"><Phone className="h-4 w-4 text-slate-400" /> {s.phone}</div>}
        </div>

        {(() => {
          const todayStr = new Date().toISOString().slice(0, 10);
          const fmtD = (x) => { try { return new Date(x + "T00:00:00").toLocaleDateString("it-IT", { day: "numeric", month: "short" }); } catch (e) { return x; } };
          const specials = (s.special_hours || [])
            .filter((x) => x && x.date && (x.date_to || x.date) >= todayStr)
            .sort((a, b) => (a.date > b.date ? 1 : -1)).slice(0, 6);
          if (!specials.length) return null;
          return (
            <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3" data-testid="store-special-hours">
              <p className="text-xs font-bold text-amber-800 mb-1.5 flex items-center gap-1.5"><CalendarClock className="h-4 w-4" /> Orari speciali e chiusure</p>
              <div className="space-y-1">
                {specials.map((sp, i) => (
                  <div key={i} className="flex justify-between gap-3 text-sm text-amber-900">
                    <span className="truncate">{fmtD(sp.date)}{sp.date_to && sp.date_to !== sp.date ? `–${fmtD(sp.date_to)}` : ""}{sp.label ? ` · ${sp.label}` : ""}</span>
                    <span className="shrink-0 font-semibold">{sp.closed ? "Chiuso" : `${sp.from}–${sp.to}`}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>

      <div className="px-4 py-5">
        {/* Recensioni clienti (verificate) */}
        <div className="mt-6" data-testid="client-reviews">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-extrabold text-lg text-slate-900 flex items-center gap-2">
              <Star className="h-5 w-5 text-amber-500" /> Recensioni clienti
              {rev.average != null && <span className="text-sm font-bold text-amber-600">{rev.average} · {rev.count}</span>}
            </h2>
            {rev.can_review && (
              <button onClick={openReview} data-testid="write-review-btn" className="h-9 px-3 rounded-lg kn-gradient text-white font-bold text-sm">
                {rev.my_review_count > 0 ? "Nuova recensione" : "Recensisci"}
              </button>
            )}
          </div>
          {getToken("client") && !rev.has_redeemed && rev.my_review_count === 0 && (
            <p className="text-xs text-slate-400 mb-2" data-testid="review-locked-hint">Puoi recensire solo dopo aver usato un coupon di questo negozio.</p>
          )}
          {getToken("client") && rev.has_redeemed && !rev.can_review && (
            <p className="text-xs text-slate-400 mb-2" data-testid="review-all-done-hint">Hai già recensito tutti i tuoi coupon usati. Usane un altro per lasciare una nuova recensione.</p>
          )}
          {(rev.groups || []).length > 0 && (
            <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-1" data-testid="review-filter-bar">
              {[
                { k: "all", label: "Tutte" },
                { k: "5", label: "5★" },
                { k: "4", label: "4★" },
                { k: "3", label: "3★" },
                { k: "low", label: "1-2★" },
              ].map((f) => (
                <button
                  key={f.k}
                  onClick={() => setReviewFilter(f.k)}
                  data-testid={`review-filter-${f.k}`}
                  className={`shrink-0 h-8 px-3 rounded-full text-sm font-bold transition-colors ${reviewFilter === f.k ? "kn-gradient text-white" : "bg-slate-100 text-slate-500"}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
          {(() => {
            const matchFilter = (g) => {
              if (reviewFilter === "all") return true;
              const r = Math.round(g.avg);
              if (reviewFilter === "low") return r <= 2;
              return r === Number(reviewFilter);
            };
            const filteredGroups = (rev.groups || []).filter(matchFilter);
            if ((rev.groups || []).length === 0) {
              return <div className="text-center py-6 text-slate-400 text-sm">Ancora nessuna recensione. Sii il primo dopo aver usato un coupon!</div>;
            }
            if (filteredGroups.length === 0) {
              return <div className="text-center py-6 text-slate-400 text-sm" data-testid="review-filter-empty">Nessuna recensione con questo voto.</div>;
            }
            return (
            <div className="space-y-3">
              {filteredGroups.map((g) => {
                const expanded = expandedGroups[g.client_account_id] || g.count === 1;
                const shown = expanded ? g.reviews : g.reviews.slice(0, 1);
                return (
                  <div key={g.client_account_id} className="kn-card p-3.5" data-testid={`client-review-group-${g.client_account_id}`}>
                    <button
                      type="button"
                      onClick={() => g.count > 1 && setExpandedGroups((p) => ({ ...p, [g.client_account_id]: !p[g.client_account_id] }))}
                      className="w-full flex items-center gap-2.5 text-left"
                      data-testid={`review-group-toggle-${g.client_account_id}`}
                    >
                      <div className="h-9 w-9 rounded-full bg-[#FFE4E6] flex items-center justify-center shrink-0 font-bold text-[#E63946]">{(g.client_name || "?")[0]}</div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-slate-800 text-sm truncate">{g.client_name}</p>
                        <div className="flex items-center gap-1 flex-wrap">
                          <div className="flex items-center gap-0.5">
                            {[...Array(5)].map((_, s) => <Star key={s} className={`h-3.5 w-3.5 ${s < Math.round(g.avg) ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}
                          </div>
                          <span className="text-xs font-bold text-amber-600">{g.avg}</span>
                          {g.count > 1 && <span className="text-xs text-slate-400">· {g.count} recensioni</span>}
                          {g.latest_at && <span className="text-xs text-slate-400">· {timeAgo(g.latest_at)}</span>}
                        </div>
                      </div>
                      {g.count > 1 && (expanded ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />)}
                    </button>
                    <div className="mt-2 space-y-2.5">
                      {shown.map((r) => (
                        <div key={r.review_id} className={g.count > 1 ? "pl-11" : ""} data-testid={`client-review-${r.review_id}`}>
                          <div className="flex items-center gap-1.5 mb-0.5">
                            {g.count > 1 && (
                              <div className="flex items-center gap-1">
                                {[...Array(5)].map((_, s) => <Star key={s} className={`h-3 w-3 ${s < r.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}
                              </div>
                            )}
                            {r.created_at && <span className="text-[11px] text-slate-400" data-testid={`review-date-${r.review_id}`}>{timeAgo(r.created_at)}</span>}
                          </div>
                          {r.comment && <p className="text-sm text-slate-600 leading-relaxed">{r.comment}</p>}
                          {r.reply && (
                            <div className="mt-2 ml-1 pl-3 border-l-2 border-[#E63946]/30 bg-slate-50 rounded-r-lg p-2.5" data-testid={`store-reply-${r.review_id}`}>
                              <p className="text-xs font-bold text-[#E63946] mb-0.5">Risposta del negozio</p>
                              <p className="text-sm text-slate-600">{r.reply}</p>
                            </div>
                          )}
                        </div>
                      ))}
                      {g.count > 1 && !expanded && (
                        <button type="button" onClick={() => setExpandedGroups((p) => ({ ...p, [g.client_account_id]: true }))} className="pl-11 text-xs font-bold text-[#E63946]" data-testid={`review-group-more-${g.client_account_id}`}>
                          Leggi tutte le {g.count} recensioni
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            );
          })()}
        </div>

        {/* Google reviews */}
        {reviews.length > 0 && (
          <div className="mt-6" data-testid="google-reviews">
            <h2 className="font-extrabold text-lg text-slate-900 mb-1 flex items-center gap-2">
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" /> Recensioni Google
              {gMeta.rating != null && (
                <span className="text-sm font-bold text-amber-500 flex items-center gap-1">
                  <Star className="h-4 w-4 fill-amber-400 text-amber-400" />{gMeta.rating}
                  {gMeta.review_count != null && <span className="text-slate-400 font-medium">· {gMeta.review_count}</span>}
                </span>
              )}
            </h2>
            <p className="text-xs text-slate-400 mb-3">Una selezione di recensioni da Google.</p>
            <div className="space-y-3">
              {reviews.map((rv, i) => (
                <div key={i} className="kn-card p-3.5">
                  <div className="flex items-center gap-2.5">
                    <div className="h-9 w-9 rounded-full bg-slate-100 overflow-hidden flex items-center justify-center shrink-0">
                      {rv.author_photo ? <img src={rv.author_photo} alt="" className="h-full w-full object-cover" /> : <span className="font-bold text-slate-500 text-sm">{(rv.author_name || "?")[0]}</span>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-slate-800 text-sm truncate">{rv.author_name || "Utente Google"}</p>
                      <p className="text-xs text-slate-400">{rv.relative_time}</p>
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      {[...Array(5)].map((_, s) => <Star key={s} className={`h-3.5 w-3.5 ${s < (rv.rating || 0) ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}
                    </div>
                  </div>
                  {rv.text && <p className="text-sm text-slate-600 mt-2 leading-relaxed line-clamp-4">{rv.text}</p>}
                </div>
              ))}
            </div>
            <a
              href={gMeta.google_maps_uri || (s?.lat && s?.lng ? `https://www.google.com/maps/search/?api=1&query=${s.lat},${s.lng}` : "https://www.google.com/maps")}
              target="_blank" rel="noopener noreferrer"
              data-testid="google-reviews-see-all"
              className="mt-3 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-[#E63946] transition-colors">
              Vedi le altre su Google <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        )}

        {/* Report */}
        <button onClick={() => setReportOpen(true)} data-testid="report-store-btn" className="mt-6 mx-auto flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#E63946] font-semibold">
          <Flag className="h-4 w-4" /> Segnala questo negozio
        </button>
      </div>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="sm:max-w-md rounded-3xl" data-testid="report-dialog">
          <DialogHeader><DialogTitle className="text-xl font-extrabold">Segnala un problema</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              {(reasons.length ? reasons : [{ id: "altro", label: "Altro" }]).map((r) => {
                const rid = r.id || r.key; const rlabel = r.label || r.id || r.key;
                return (
                  <button key={rid} onClick={() => setReport({ ...report, reason: rid })} data-testid={`reason-${rid}`}
                    className={`w-full text-left p-3 rounded-xl border font-semibold text-sm transition-colors ${report.reason === rid ? "border-[#E63946] bg-[#FFF1F2] text-[#BE123C]" : "border-slate-200 text-slate-600"}`}>
                    {rlabel}
                  </button>
                );
              })}
            </div>
            <Textarea value={report.note} onChange={(e) => setReport({ ...report, note: e.target.value })} rows={3} placeholder="Aggiungi dettagli (opzionale)" className="rounded-xl" data-testid="report-note" />
            <Button onClick={submitReport} disabled={sendingReport} data-testid="report-submit" className="w-full h-12 rounded-xl font-bold">
              {sendingReport ? <Loader2 className="h-5 w-5 animate-spin" /> : "Invia segnalazione"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="sm:max-w-md rounded-3xl" data-testid="review-dialog">
          <DialogHeader><DialogTitle className="text-xl font-extrabold">La tua recensione</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-2 py-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setMyRating(n)} data-testid={`star-${n}`} className="transition-transform hover:scale-110">
                  <Star className={`h-9 w-9 ${n <= myRating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
                </button>
              ))}
            </div>
            <Textarea value={myComment} onChange={(e) => setMyComment(e.target.value)} rows={4} placeholder="Racconta la tua esperienza (opzionale)" className="rounded-xl" data-testid="review-comment" />
            <Button onClick={submitReview} disabled={savingReview} data-testid="review-submit" className="w-full h-12 rounded-xl font-bold">
              {savingReview ? <Loader2 className="h-5 w-5 animate-spin" /> : "Pubblica recensione"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
